import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useSession } from '@/hooks';
import { Button, Dialog, MetaRow, Textarea } from '@/ui';
import { api } from '@/core/lib/apiClient';
import { discussionHref } from '@/core/services/discussionsService';
import { FollowedDiscussions } from '@/features/discussions/FollowedDiscussions';
import { discussionTools, type Library, type Invitation } from './service';
import { Card, Field, Row, Small, Stack } from './styles';

export function InvitationPreference() {
  const { currentUser } = useSession();
  const [value, setValue] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    if (currentUser)
      void api<{ invitations: string }>('/discussion-tools/preferences/')
        .then((p) => {
          if (live) setValue(p.invitations);
        })
        .catch(() => {
          if (live) setError('Could not load invitation preferences.');
        });
    return () => {
      live = false;
    };
  }, [currentUser?.id]);
  if (!currentUser) return null;
  return (
    <Stack>
      <Field>
        Who can invite you to a discussion?
        <select
          value={value ?? 'following'}
          disabled={value === null || busy}
          onChange={(e) => {
            setBusy(true);
            setError('');
            const next = e.target.value;
            void api<{ invitations: string }>('/discussion-tools/preferences/', {
              method: 'PATCH',
              body: { invitations: next },
            })
              .then((p) => setValue(p.invitations))
              .catch(() => setError('Could not save. Your previous choice is unchanged.'))
              .finally(() => setBusy(false));
          }}
        >
          <option value="following">People I follow</option>
          <option value="everyone">Everyone</option>
          <option value="nobody">Nobody</option>
        </select>
      </Field>
      <Small>
        Invitations arrive in your discussion inbox. Accepting follows silently; notifications stay
        under your control.
      </Small>
      {error && <p role="alert">{error}</p>}
      <Link to="/discussions?tab=invitations">Open discussion invitations</Link>
    </Stack>
  );
}

export function DiscussionLibrary() {
  const { currentUser } = useSession();
  if (!currentUser) return <Navigate to="/signin?next=%2Fdiscussions" replace />;
  return <LibraryContent key={currentUser.id} />;
}
function LibraryContent() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'discussions';
  const [data, setData] = useState<Library | null>(null);
  const [invites, setInvites] = useState<Invitation[]>([]);
  const [label, setLabel] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<Library['bookmarks'][number] | null>(null);
  const [note, setNote] = useState('');
  async function load() {
    setBusy(true);
    setError('');
    try {
      const [l, i] = await Promise.all([discussionTools.library(), discussionTools.invitations()]);
      setData(l);
      setInvites(i);
    } catch {
      setError('Could not load your discussion library. Try again.');
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    let live = true;
    void Promise.all([discussionTools.library(), discussionTools.invitations()])
      .then(([l, i]) => {
        if (live) {
          setData(l);
          setInvites(i);
        }
      })
      .catch(() => {
        if (live) setError('Could not load your discussion library. Try again.');
      });
    return () => {
      live = false;
    };
  }, []);
  async function respond(id: number, state: string) {
    setBusy(true);
    setError('');
    try {
      setInvites(
        await api('/discussion-tools/invitations/', { method: 'PATCH', body: { id, state } }),
      );
    } catch {
      setError('Could not respond. The invitation may no longer be available.');
    } finally {
      setBusy(false);
    }
  }
  async function saveNote(remove = false) {
    if (!editing) return;
    setBusy(true);
    setError('');
    try {
      await discussionTools.save(editing.postId, {
        action: 'bookmark',
        commentId: editing.commentId,
        note,
        remove,
      });
      setData(await discussionTools.library());
      setEditing(null);
    } catch {
      setError('Could not save this bookmark. Try again.');
    } finally {
      setBusy(false);
    }
  }
  const labels = [...new Set(data?.discussions.flatMap((d) => d.labels) ?? [])];
  return (
    <Stack>
      <h1>Your discussions</h1>
      <Row>
        {[
          ['discussions', 'Discussions'],
          ['bookmarks', 'Private notes'],
          ['invitations', 'Invitations'],
        ].map(([id, title]) => (
          <Button
            key={id}
            variant={tab === id ? 'secondary' : 'ghost'}
            onClick={() => setParams({ tab: id! })}
          >
            {id === 'invitations' && invites.some((i) => i.incoming && i.state === 'pending') ? (
              <MetaRow>
                <span>{title}</span>
                <span>New</span>
              </MetaRow>
            ) : (
              title
            )}
          </Button>
        ))}
        <Button variant="ghost" disabled={busy} onClick={() => void load()}>
          Refresh
        </Button>
      </Row>
      {error && <p role="alert">{error}</p>}
      {!data && !error && <p role="status">Loading your discussions…</p>}
      {tab === 'discussions' && (
        <>
          <FollowedDiscussions manage />
          <h2>Private labels</h2>
          <Field>
            Filter by label
            <select value={label} onChange={(e) => setLabel(e.target.value)}>
              <option value="">All labels</option>
              {labels.map((l) => (
                <option key={l}>{l}</option>
              ))}
            </select>
          </Field>
          {data?.discussions
            .filter((d) => !label || d.labels.includes(label))
            .map((d) => (
              <Card key={d.postId}>
                <Link to={discussionHref(d.postId)}>{d.body || 'Open discussion'}</Link>
                <Small>
                  <MetaRow>
                    {d.labels.map((l) => (
                      <span key={l}>{l}</span>
                    ))}
                  </MetaRow>
                </Small>
              </Card>
            ))}
          {data && !data.discussions.length && (
            <Small>Open a post and choose Labels to organize it privately.</Small>
          )}
        </>
      )}
      {tab === 'bookmarks' && (
        <>
          <Small>Private notes on saved posts and comments. Only your account can see these.</Small>
          {data?.bookmarks.map((b) => (
            <Card key={`${b.postId}:${b.commentId}`}>
              <Link to={discussionHref(b.postId, b.commentId)}>
                {b.body.slice(0, 500) || 'Open saved item'}
              </Link>
              {b.note && <p>{b.note}</p>}
              <Button
                variant="ghost"
                onClick={() => {
                  setEditing(b);
                  setNote(b.note);
                }}
              >
                Edit note or remove bookmark
              </Button>
            </Card>
          ))}
          {data && !data.bookmarks.length && <Small>No saved discussions yet.</Small>}
        </>
      )}
      {tab === 'invitations' && (
        <>
          <InvitationPreference />
          {invites.map((i) => (
            <Card key={i.id}>
              <strong>{i.incoming ? `From ${i.sender}` : `To ${i.recipient}`}</strong>
              <Small>
                <MetaRow>
                  <span>{i.state}</span>
                  <span>{new Date(i.createdAt).toLocaleString()}</span>
                </MetaRow>
              </Small>
              {i.postId ? (
                <Link to={discussionHref(i.postId)}>View discussion</Link>
              ) : (
                <Small>Discussion unavailable</Small>
              )}
              {i.incoming && i.state === 'pending' && (
                <Row>
                  <Button
                    disabled={busy || !i.postId}
                    onClick={() => void respond(i.id, 'accepted')}
                  >
                    Accept and follow silently
                  </Button>
                  <Button
                    disabled={busy}
                    variant="ghost"
                    onClick={() => void respond(i.id, 'declined')}
                  >
                    Decline
                  </Button>
                </Row>
              )}
            </Card>
          ))}
          {!invites.length && <Small>No discussion invitations yet.</Small>}
        </>
      )}
      {editing && (
        <Dialog
          open
          title="Private bookmark note"
          onClose={() => {
            if (!busy) setEditing(null);
          }}
          dismissable={!busy}
        >
          <Stack>
            <Textarea
              label="Private note"
              maxLength={5000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <Row>
              <Button disabled={busy} onClick={() => void saveNote()}>
                Save note
              </Button>
              <Button disabled={busy} variant="ghost" onClick={() => void saveNote(true)}>
                Remove bookmark
              </Button>
              <Button disabled={busy} variant="ghost" onClick={() => setEditing(null)}>
                Cancel
              </Button>
            </Row>
            {error && <p role="alert">{error}</p>}
          </Stack>
        </Dialog>
      )}
    </Stack>
  );
}
